<template>
  <el-dialog
    title="头像采集"
    :visible.sync="visible"
    width="640px"
    :close-on-click-modal="false"
    :append-to-body="true"
    @close="handleClose"
  >
    <!-- 模式切换 -->
    <div v-if="mode === 'select'" class="mode-select">
      <el-button type="primary" @click="startCamera">
        使用摄像头
      </el-button>
      <el-button @click="mode = 'upload'">
        上传图片
      </el-button>
    </div>

    <!-- 摄像头模式 -->
    <div v-else-if="mode === 'camera'" class="camera-mode">
      <div class="device-select" v-if="videoDevices.length > 1">
        <el-select v-model="selectedDeviceId" size="small" placeholder="选择摄像头" @change="switchDevice">
          <el-option v-for="device in videoDevices" :key="device.deviceId" :label="device.label || '摄像头'" :value="device.deviceId" />
        </el-select>
      </div>
      <div class="sharpness-select">
        <span class="label">清晰度要求：</span>
        <el-select v-model="sharpnessPreset" size="small" placeholder="选择清晰度" style="width: 160px">
          <el-option v-for="preset in SHARPNESS_PRESETS" :key="preset.value" :label="preset.label" :value="preset.value" />
        </el-select>
        <span class="sharpness-value" v-if="hasVideoSignal">当前清晰度: {{ Math.round(sharpness) }}</span>
      </div>
      <div class="video-wrapper">
        <video ref="video" autoplay playsinline muted />
        <div v-if="!hasVideoSignal" class="no-signal">
          <i class="el-icon-video-camera" />
          <p>{{ noSignalText }}</p>
        </div>
        <canvas ref="overlay" class="overlay" />
      </div>
      <div class="status-bar">
        <el-tag :type="statusType">{{ statusText }}</el-tag>
      </div>
      <div class="actions">
        <el-button type="primary" :disabled="!canCapture" @click="capture">
          拍照
        </el-button>
        <el-button @click="reloadCamera">
          刷新摄像头
        </el-button>
        <el-button @click="switchToUpload">
          改用上传
        </el-button>
      </div>
    </div>

    <!-- 上传模式 -->
    <div v-else-if="mode === 'upload'" class="upload-mode">
      <el-upload
        drag
        action=""
        :auto-upload="false"
        :show-file-list="false"
        :on-change="handleFileChange"
        accept="image/jpeg,image/jpg,image/png"
      >
        <i class="el-icon-upload" />
        <div class="el-upload__text">
          将图片拖到此处，或点击上传
        </div>
      </el-upload>
    </div>

    <!-- 预览确认 -->
    <div v-else-if="mode === 'preview'" class="preview-mode">
      <el-image :src="previewUrl" style="width: 300px; height: 400px" fit="cover" />
      <div class="actions">
        <el-button type="primary" @click="confirm">
          确认使用
        </el-button>
        <el-button @click="retake">
          重新拍摄
        </el-button>
      </div>
    </div>

    <div slot="footer">
      <el-button @click="visible = false">
        取消
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  loadModels,
  detectFace,
  estimateSharpness,
  cropFace,
  compressToSize,
  fileToImage
} from '@/utils/faceUtils'
import { uploadFile } from '@/api/staff/addStaffApi'
import { getCommunityId } from '@/api/community/communityApi'

const SHARPNESS_PRESETS = [
  { label: '宽松（50）', value: 50 },
  { label: '较宽松（80）', value: 80 },
  { label: '标准（120）', value: 120 },
  { label: '较严格（180）', value: 180 },
  { label: '严格（200）', value: 200 }
]
const DEFAULT_SHARPNESS = 120
const DETECT_INTERVAL = 200

export default {
  name: 'FaceCaptureDialog',
  props: {
    outputWidth: { type: Number, default: 300 },
    outputHeight: { type: Number, default: 400 },
    maxSizeKb: { type: Number, default: 200 },
    strict: { type: Boolean, default: true }
  },
  data() {
    return {
      visible: false,
      mode: 'select',
      stream: null,
      detectTimer: null,
      currentFace: null,
      sharpness: 0,
      sharpnessPreset: DEFAULT_SHARPNESS,
      SHARPNESS_PRESETS,
      capturedBlob: null,
      previewUrl: '',
      status: 'idle',
      videoDevices: [],
      selectedDeviceId: '',
      hasVideoSignal: false,
      noSignalText: '正在连接摄像头...'
    }
  },
  computed: {
    sharpnessThreshold() {
      return this.sharpnessPreset || DEFAULT_SHARPNESS
    },
    canCapture() {
      return this.hasVideoSignal && this.currentFace !== null && this.sharpness >= this.sharpnessThreshold
    },
    statusType() {
      if (!this.hasVideoSignal) return 'danger'
      if (this.status === 'ready') return 'success'
      if (this.status === 'noFace' || this.status === 'blur') return 'warning'
      return 'info'
    },
    statusText() {
      const map = {
        idle: '正在启动摄像头...',
        noFace: '请将人脸对准取景框',
        blur: `画面模糊，请保持稳定（当前 ${Math.round(this.sharpness)} < ${this.sharpnessThreshold}）`,
        ready: `可以拍照（清晰度 ${Math.round(this.sharpness)}）`,
        loading: '正在加载模型...',
        noSignal: '未检测到摄像头信号',
        noDevice: '未找到摄像头设备',
        permissionDenied: '摄像头权限被拒绝',
        inUse: '摄像头被其他程序占用'
      }
      return map[this.status] || '准备中'
    }
  },
  methods: {
    open(initialMode = 'select') {
      this.visible = true
      this.mode = initialMode
      this.currentFace = null
      this.sharpness = 0
      this.capturedBlob = null
      this.previewUrl = ''
    },
    handleClose() {
      this.stopCamera()
      this.clearDetectTimer()
      this.revokePreviewUrl()
      this.mode = 'select'
    },
    async startCamera() {
      this.mode = 'camera'
      this.status = 'loading'
      this.hasVideoSignal = false
      this.noSignalText = '正在连接摄像头...'
      try {
        await loadModels()
        await this.enumerateDevices()

        // 若之前缓存的 selectedDeviceId 已不在当前设备列表中（如设备被拔出），
        // 自动回退到第一个可用设备，避免首次打开就因 exact 约束失败。
        const stillAvailable = this.videoDevices.some(d => d.deviceId === this.selectedDeviceId)
        if (!stillAvailable && this.videoDevices.length > 0) {
          this.selectedDeviceId = this.videoDevices[0].deviceId
          console.log('缓存设备不可用，自动切换到:', this.selectedDeviceId)
        }

        await this.openStream()
      } catch (error) {
        this.handleCameraError(error)
      }
    },
    async enumerateDevices() {
      try {
        let devices = await navigator.mediaDevices.enumerateDevices()
        this.videoDevices = devices.filter(d => d.kind === 'videoinput')

        // 首次授权前 enumerateDevices 返回的设备 label 为空，deviceId 也可能为空。
        // 先请求一次临时权限，确保拿到带 label 的真实设备列表，便于用户区分摄像头。
        const hasRealDeviceId = this.videoDevices.some(d => d.deviceId)
        if (!hasRealDeviceId && navigator.mediaDevices.getUserMedia) {
          const tempStream = await navigator.mediaDevices.getUserMedia({ video: true })
          tempStream.getTracks().forEach(track => track.stop())
          devices = await navigator.mediaDevices.enumerateDevices()
          this.videoDevices = devices.filter(d => d.kind === 'videoinput')
        }

        console.log('可用摄像头:', this.videoDevices)
        if (this.videoDevices.length > 0 && !this.selectedDeviceId) {
          this.selectedDeviceId = this.videoDevices[0].deviceId
        }
      } catch (error) {
        console.error('枚举设备失败', error)
      }
    },
    async openStream() {
      this.stopCamera()
      this.hasVideoSignal = false
      this.noSignalText = '正在连接摄像头...'

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('浏览器不支持摄像头')
      }

      const constraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      }
      if (this.selectedDeviceId) {
        constraints.video.deviceId = { exact: this.selectedDeviceId }
      }

      console.log('正在打开摄像头, deviceId:', this.selectedDeviceId)
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      this.stream = stream

      await this.$nextTick()
      const video = this.$refs.video
      if (!video) {
        throw new Error('视频元素未找到')
      }
      video.srcObject = stream

      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('摄像头信号超时')), 10000)

        const onCanPlay = () => {
          clearTimeout(timeout)
          cleanup()
          resolve()
        }
        const onError = () => {
          clearTimeout(timeout)
          cleanup()
          reject(new Error('视频加载失败'))
        }
        const cleanup = () => {
          video.removeEventListener('canplay', onCanPlay)
          video.removeEventListener('error', onError)
        }

        video.addEventListener('canplay', onCanPlay)
        video.addEventListener('error', onError)

        // 如果视频已经可播放，立即 resolve，避免事件已触发而漏听
        if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
          onCanPlay()
        }
      })

      await video.play()
      this.hasVideoSignal = true
      this.status = 'noFace'
      // 记录实际分辨率，便于排查清晰度为 0 的问题
      console.log('摄像头实际分辨率:', video.videoWidth, 'x', video.videoHeight)
      this.startDetectLoop()

      // 如果第一帧没数据，延迟再检查一次
      setTimeout(() => {
        if (video.readyState < 2) {
          this.hasVideoSignal = false
          this.status = 'noSignal'
          this.noSignalText = '摄像头无视频信号，请检查连接或权限'
        }
      }, 1500)
    },
    handleCameraError(error) {
      console.error('摄像头启动失败', error)
      this.hasVideoSignal = false
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        this.status = 'permissionDenied'
        this.noSignalText = '摄像头权限被拒绝，请在浏览器地址栏允许访问摄像头'
        this.$message.error('摄像头权限被拒绝，请允许浏览器访问摄像头')
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        this.status = 'noDevice'
        this.noSignalText = '未找到摄像头设备，请检查 USB 连接'
        this.$message.error('未找到摄像头设备')
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        this.status = 'inUse'
        this.noSignalText = '摄像头被其他程序占用，请关闭微信/腾讯会议/相机等软件'
        this.$message.error('摄像头被占用')
      } else {
        this.status = 'noSignal'
        this.noSignalText = error.message || '无法启动摄像头'
        this.$message.error(error.message || '无法启动摄像头')
      }
    },
    async switchDevice() {
      try {
        this.status = 'loading'
        await this.openStream()
      } catch (error) {
        this.handleCameraError(error)
      }
    },
    async reloadCamera() {
      // 刷新时重新枚举设备，若当前选中的设备已不在列表中（如被拔出），
      // 自动回退到第一个可用设备，避免用户每次都要手动选择。
      await this.enumerateDevices()
      const stillAvailable = this.videoDevices.some(d => d.deviceId === this.selectedDeviceId)
      if (!stillAvailable && this.videoDevices.length > 0) {
        this.selectedDeviceId = this.videoDevices[0].deviceId
        console.log('当前设备不可用，自动切换到:', this.selectedDeviceId)
      }
      await this.switchDevice()
    },
    stopCamera() {
      this.hasVideoSignal = false
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      if (this.$refs.video) {
        this.$refs.video.srcObject = null
      }
    },
    clearDetectTimer() {
      if (this.detectTimer) {
        clearInterval(this.detectTimer)
        this.detectTimer = null
      }
    },
    revokePreviewUrl() {
      if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = ''
      }
    },
    startDetectLoop() {
      this.clearDetectTimer()
      this.detectTimer = setInterval(() => {
        this.detectFrame()
      }, DETECT_INTERVAL)
    },
    async detectFrame() {
      const video = this.$refs.video
      // 放宽 readyState 要求：只要有当前帧数据即可检测，避免摄像头启动阶段
      // readyState 长时间在 2/3 时清晰度始终为 0。
      if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        console.log('视频未就绪，跳过检测，readyState:', video?.readyState)
        return
      }
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.warn('视频尺寸为 0，跳过本次检测')
        return
      }

      // 先绘制当前帧并计算整帧清晰度，用于无人脸时给用户参考
      const fullCanvas = document.createElement('canvas')
      fullCanvas.width = video.videoWidth
      fullCanvas.height = video.videoHeight
      const fullCtx = fullCanvas.getContext('2d')
      fullCtx.drawImage(video, 0, 0)
      const fullSharpness = estimateSharpness(fullCanvas)

      let detection = null
      try {
        detection = await detectFace(video)
      } catch (error) {
        console.error('人脸检测失败:', error)
      }
      this.drawOverlay(detection)

      if (!detection) {
        this.currentFace = null
        // 无人脸时显示整帧清晰度，避免用户误以为摄像头无信号
        this.sharpness = fullSharpness
        this.status = 'noFace'
        console.log('未检测到人脸，整帧清晰度:', fullSharpness)
        return
      }

      this.currentFace = detection

      // 计算人脸区域清晰度，排除背景干扰，结果更准确
      const faceCanvas = document.createElement('canvas')
      faceCanvas.width = Math.max(1, Math.round(detection.width))
      faceCanvas.height = Math.max(1, Math.round(detection.height))
      const faceCtx = faceCanvas.getContext('2d')
      faceCtx.drawImage(
        fullCanvas,
        detection.x, detection.y, detection.width, detection.height,
        0, 0, faceCanvas.width, faceCanvas.height
      )
      this.sharpness = estimateSharpness(faceCanvas)

      console.log('检测到人脸，清晰度:', this.sharpness, '人脸框:', detection)

      if (this.sharpness < this.sharpnessThreshold) {
        this.status = 'blur'
      } else {
        this.status = 'ready'
      }
    },
    drawOverlay(detection) {
      const video = this.$refs.video
      const overlay = this.$refs.overlay
      if (!video || !overlay) return

      overlay.width = video.offsetWidth
      overlay.height = video.offsetHeight
      const ctx = overlay.getContext('2d')
      ctx.clearRect(0, 0, overlay.width, overlay.height)

      if (!detection) return

      const scaleX = overlay.width / video.videoWidth
      const scaleY = overlay.height / video.videoHeight
      const x = detection.x * scaleX
      const y = detection.y * scaleY
      const w = detection.width * scaleX
      const h = detection.height * scaleY

      ctx.strokeStyle = '#67c23a'
      ctx.lineWidth = 3
      ctx.strokeRect(x, y, w, h)
    },
    async capture() {
      const video = this.$refs.video
      if (!video) return

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0)

      try {
        await this.processImage(canvas)
      } catch (error) {
        this.$message.error(error.message || '照片处理失败')
      }
    },
    switchToUpload() {
      this.stopCamera()
      this.clearDetectTimer()
      this.mode = 'upload'
    },
    async handleFileChange(file) {
      const raw = file.raw
      if (!raw) return
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(raw.type)) {
        this.$message.error('仅支持 JPG/JPEG/PNG 格式')
        return
      }
      try {
        await loadModels()
        const img = await fileToImage(raw)
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)
        await this.processImage(canvas)
      } catch (error) {
        console.error(error)
        this.$message.error(error.message || '图片处理失败')
      }
    },
    async processImage(sourceCanvas) {
      const detection = await detectFace(sourceCanvas)
      if (!detection) {
        throw new Error('未检测到人脸，请调整姿势或更换照片')
      }

      const sharpness = estimateSharpness(sourceCanvas)
      if (this.strict && sharpness < this.sharpnessThreshold) {
        throw new Error('照片清晰度不足，请重新拍摄')
      }

      const croppedCanvas = cropFace(sourceCanvas, detection, this.outputWidth, this.outputHeight)
      const finalBlob = await compressToSize(croppedCanvas, this.maxSizeKb)
      this.capturedBlob = finalBlob
      this.revokePreviewUrl()
      this.previewUrl = URL.createObjectURL(finalBlob)
      this.mode = 'preview'
    },
    retake() {
      this.capturedBlob = null
      this.revokePreviewUrl()
      if (this.stream) {
        this.mode = 'camera'
        this.$nextTick(() => {
          this.startDetectLoop()
        })
      } else {
        this.mode = 'upload'
      }
    },
    async confirm() {
      if (!this.capturedBlob) return
      try {
        const formData = new FormData()
        formData.append('uploadFile', this.capturedBlob, 'face.jpg')
        const communityId = getCommunityId ? getCommunityId() : '-1'
        formData.append('communityId', communityId)
        const res = await uploadFile(formData)
        this.$emit('success', { url: res.url, fileId: res.fileId })
        this.visible = false
      } catch (error) {
        console.error('上传失败', error)
        this.$message.error(error.message || '上传失败')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mode-select {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 40px 0;
}

.camera-mode {
  .device-select,
  .sharpness-select {
    margin-bottom: 12px;
    text-align: center;

    .el-select {
      width: 160px;
    }
  }

  .sharpness-select {
    .label {
      font-size: 14px;
      color: #606266;
    }

    .sharpness-value {
      margin-left: 12px;
      font-size: 14px;
      color: #909399;
    }
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    height: 360px;
    background: #000;
    display: flex;
    justify-content: center;
    align-items: center;

    video {
      max-width: 100%;
      max-height: 100%;
    }

    .no-signal {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      color: #fff;
      background: rgba(0, 0, 0, 0.7);
      z-index: 2;

      i {
        font-size: 48px;
        margin-bottom: 12px;
      }

      p {
        font-size: 14px;
      }
    }

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 3;
    }
  }

  .status-bar {
    margin-top: 12px;
    text-align: center;
  }

  .actions {
    margin-top: 16px;
    text-align: center;
  }
}

.upload-mode {
  text-align: center;
  padding: 20px 0;
}

.preview-mode {
  text-align: center;

  .actions {
    margin-top: 16px;
  }
}
</style>
